import React from 'react';
import {useNavigate} from 'react-router-dom';
import {BeakerIcon, FunnelIcon, PencilIcon, TrashIcon} from '@heroicons/react/24/outline';
import {BrandSafetyPrompt, Company, PaginationInfo} from '../../types';
import {ScheduledJobSummary} from '../../services/api';
import {formatCurrency} from '../../utils/format';
import StrategySimulatorPanel from '../StrategySimulatorPanel';
import DeliveryFunnelSection, {DeliveryFunnelOpenDomainsPayload} from '../DeliveryFunnelSection';
import ActionTooltip from './ActionTooltip';
import {companyColor} from './companyColor';
import {useChartMode} from '../charts/useChartMode';
import {canManage, isDeleteDisabled} from './tableRules';
import {isJobExpired} from '../scheduledJobs/jobStatus';
import {TableFillBox, tableFillHeightClass} from '../ui/tableFillBox';
import {ActionIconButton} from '../ui/ActionIconButton';
import Pagination from '../ui/pagination';
import {Table, TableHead, TableHeaderCell, TableBody, TableRow, TableCell} from '../ui/table';

interface TableUser {
    id: string;
    role: 'user' | 'eskimi_admin' | 'adops' | 'admin';
    companyId?: string | null;
}

interface BlueprintsTableProps {
    prompts: BrandSafetyPrompt[];
    isLoading: boolean;
    isFullPage: boolean;
    searchTerm: string;
    showOnlyMyBlueprints: boolean;
    isAdmin: boolean;
    user: TableUser | null | undefined;
    companies: Company[];
    connectedJobsFor: (promptId: string) => ScheduledJobSummary[];
    estScanCostFor: (promptId: string) => number | null;
    strategySimulatorEnabled: boolean;
    expandedSimulatorPromptId: string | null;
    setExpandedSimulatorPromptId: (id: string | null) => void;
    expandedFunnelPromptId: string | null;
    setExpandedFunnelPromptId: (id: string | null) => void;
    onEdit: (prompt: BrandSafetyPrompt) => void;
    onDelete: (id: string) => void;
    onOpenDomains: (payload: DeliveryFunnelOpenDomainsPayload) => void;
    // Optional: when supplied, pagination renders as the shared ghost-bg
    // footer INSIDE this card (deepcontext-c0p7.3) instead of the caller
    // floating its own block below. Omitted entirely by callers/tests that
    // don't page.
    paginationInfo?: PaginationInfo;
    onPageChange?: (page: number) => void;
}

// Blueprints table (Variant C). Connected Jobs column is admin-only. Sizing is
// conditional on the TOTAL result count (bd-n3uyh) via the shared TableFillBox
// (deepcontext-wbra — every paginated table uses this, not a copy-pasted
// ternary): a full page (or more) FILLS the column down to the pagination
// (flex-1) and scrolls internally on short screens; a sparse result set HUGS
// its content so a few rows never stretch into a tall empty box. Extracted
// from PromptManager.tsx (deepcontext-ityf decomposition); restyled to the
// locked design in deepcontext-pnxk.
const BlueprintsTable: React.FC<BlueprintsTableProps> = ({
    prompts,
    isLoading,
    isFullPage,
    searchTerm,
    showOnlyMyBlueprints,
    isAdmin,
    user,
    companies,
    connectedJobsFor,
    estScanCostFor,
    strategySimulatorEnabled,
    expandedSimulatorPromptId,
    setExpandedSimulatorPromptId,
    expandedFunnelPromptId,
    setExpandedFunnelPromptId,
    onEdit,
    onDelete,
    onOpenDomains,
    paginationInfo,
    onPageChange,
}) => {
    const navigate = useNavigate();
    const themeMode = useChartMode();
    const footer = paginationInfo && onPageChange && paginationInfo.totalPages > 1
        ? <Pagination pagination={paginationInfo} onPageChange={onPageChange} />
        : undefined;

    return (
        <TableFillBox isFullPage={isFullPage} footer={footer} className="rounded-2xl border border-line bg-surface shadow-sm">
            {isLoading ? (
                <div className="flex justify-center items-center py-12" data-testid="blueprints-loading">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-accent"></div>
                </div>
            ) : prompts.length === 0 ? (
                <div className="p-8 text-center">
                    <p className="text-ink-muted">
                        {searchTerm || showOnlyMyBlueprints ?
                            'No blueprints match your search criteria.' :
                            'No blueprints created yet. Create your first brand blueprint to get started.'}
                    </p>
                </div>
            ) : (
                <Table className={`${tableFillHeightClass(isFullPage)} min-w-full table-auto`} data-testid="blueprints-table">
                    <TableHead>
                        <tr>
                            {/* Name absorbs the width freed by Actions going
                                content-width so the layout matches production's
                                density instead of leaving a dead band between
                                Updated and Actions. For admins the percentages
                                must sum well under 100% (32+12+10+13+9+11 = 87)
                                or the content-width Actions column forces the
                                table wider than its card (deepcontext-ygo1). */}
                            <TableHeaderCell scope="col" className={isAdmin ? 'w-[32%]' : 'w-[45%]'}>Name</TableHeaderCell>
                            <TableHeaderCell scope="col" className="hidden w-[12%] lg:table-cell">Company</TableHeaderCell>
                            <TableHeaderCell scope="col" className="w-[10%]">Countries</TableHeaderCell>
                            {isAdmin && (
                                <TableHeaderCell scope="col" className="w-[13%]">Connected Jobs</TableHeaderCell>
                            )}
                            {isAdmin && (
                                <TableHeaderCell scope="col" className="w-[9%]">Est. cost</TableHeaderCell>
                            )}
                            <TableHeaderCell scope="col" className="w-[11%]">Updated</TableHeaderCell>
                            {/* Content-width, not a percentage: with table-auto
                                (not table-fixed) an unspecified/whitespace-nowrap
                                column sizes to its widest cell — up to 4 action
                                icons (28px circle + 4px gap each) — instead of
                                claiming a proportional share of the table that
                                left a dead gap between Updated and the icons. */}
                            <TableHeaderCell scope="col" className="whitespace-nowrap text-right">Actions</TableHeaderCell>
                        </tr>
                    </TableHead>
                    <TableBody>
                        {prompts.map((prompt) => {
                            const isDefault = !prompt.companyId;
                            const canManagePrompt = user ? canManage(prompt, user) : false;
                            const showCompanyTag =
                                !!prompt.companyId &&
                                (user?.role === 'admin' || !(user?.companyId && user.companyId === prompt.companyId));
                            const countryList = prompt.countries
                                ? prompt.countries.split(',').map(c => c.trim()).filter(Boolean)
                                : [];
                            const connectedJobs = connectedJobsFor(prompt.id);
                            const estScanCost = estScanCostFor(prompt.id);
                            const colSpan = isAdmin ? 7 : 5;
                            // Toggling a view panel drives the detail drawer:
                            // it is visible only while one of the panels is open
                            // (see expandedFunnelPromptId / expandedSimulatorPromptId
                            // below), so closing both collapses the drawer band too.
                            const funnelOpen = expandedFunnelPromptId === prompt.id;
                            const simulatorOpen = expandedSimulatorPromptId === prompt.id;
                            const openFunnel = () => {
                                const opening = !funnelOpen;
                                setExpandedFunnelPromptId(opening ? prompt.id : null);
                                if (opening) {
                                    setExpandedSimulatorPromptId(null);
                                }
                            };
                            const openSimulator = () => {
                                const opening = !simulatorOpen;
                                setExpandedSimulatorPromptId(opening ? prompt.id : null);
                                if (opening) {
                                    setExpandedFunnelPromptId(null);
                                }
                            };
                            const companyName = companies.find(c => c.id === prompt.companyId)?.name;
                            const companyLabel = companyName || (prompt.companyId ? 'Company' : '');
                            const swatch = companyLabel
                                ? companyColor(prompt.companyId || companyLabel, themeMode)
                                : null;
                            return (
                                <React.Fragment key={prompt.id}>
                                    <TableRow>
                                        {/* Name + tags */}
                                        <TableCell className="align-middle">
                                            <div className="flex flex-wrap items-center gap-2">
                                                {canManagePrompt ? (
                                                    // Clicking the title opens the same Edit modal as the
                                                    // pencil action button (handleEdit). Gated by canManage so
                                                    // it mirrors the pencil's ownership/admin gating exactly.
                                                    <button
                                                        type="button"
                                                        onClick={() => onEdit(prompt)}
                                                        className="rounded text-left text-sm font-extrabold text-accent-active hover:underline focus:outline-none focus:ring-2 focus:ring-accent"
                                                        aria-label={`Edit ${prompt.name}`}
                                                    >
                                                        {prompt.name}
                                                    </button>
                                                ) : (
                                                    <span className="text-sm font-extrabold text-accent-active">{prompt.name}</span>
                                                )}
                                                {isDefault && (
                                                    <span className="rounded-full bg-safe-bg px-2 py-0.5 text-xs font-bold tracking-wide text-safe">
                                                        DEFAULT
                                                    </span>
                                                )}
                                            </div>
                                            {prompt.description && (
                                                <p className="mt-0.5 line-clamp-2 max-w-[260px] text-xs leading-[1.4] text-ink-muted" title={prompt.description}>
                                                    {prompt.description}
                                                </p>
                                            )}
                                        </TableCell>
                                        {/* Company (color-coded, client-side derived color) */}
                                        <TableCell className="hidden overflow-hidden align-middle lg:table-cell">
                                            {showCompanyTag && swatch ? (
                                                <span
                                                    className="inline-block max-w-full truncate whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-bold"
                                                    style={{backgroundColor: swatch.bg, color: swatch.text}}
                                                    data-testid="company-label"
                                                    title={companyLabel}
                                                >
                                                    {companyLabel}
                                                </span>
                                            ) : (
                                                <span className="text-sm text-ink-muted">—</span>
                                            )}
                                        </TableCell>
                                        {/* Countries */}
                                        <TableCell className="align-middle text-sm">
                                            {countryList.length > 0
                                                ? countryList.map(c => c.toUpperCase()).join(', ')
                                                : <span className="text-ink-muted">All countries</span>}
                                        </TableCell>
                                        {/* Connected Jobs (admin only) — ghost pills with a
                                            live/paused status dot. */}
                                        {isAdmin && (
                                            <TableCell className="overflow-hidden align-middle">
                                                {connectedJobs.length === 0 ? (
                                                    <span className="text-sm text-ink-muted">—</span>
                                                ) : (
                                                    <div className="flex min-w-0 flex-wrap gap-1.5">
                                                        {connectedJobs.map(job => {
                                                            // An active job past its end date no longer
                                                            // runs (deepcontext-ve6o) — show it dormant.
                                                            const expired = isJobExpired({endDate: job.endDate ?? undefined});
                                                            const live = job.isActive && !expired;
                                                            return (
                                                                <button
                                                                    key={job.id}
                                                                    type="button"
                                                                    onClick={() => navigate(`/scheduled-jobs?jobId=${encodeURIComponent(job.id)}`)}
                                                                    title={
                                                                        expired && job.endDate
                                                                            ? `Ended ${new Date(job.endDate).toLocaleDateString()}`
                                                                            : job.nextRunAt
                                                                                ? `Next run: ${new Date(job.nextRunAt).toLocaleString()}`
                                                                                : job.isActive ? 'Active' : 'Paused'
                                                                    }
                                                                    aria-label={`View scheduled job ${job.name}`}
                                                                    className="inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-full bg-ghost-bg px-2.5 py-0.5 text-xs font-bold text-ink hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-accent"
                                                                >
                                                                    <span
                                                                        className={`h-1.5 w-1.5 shrink-0 rounded-full ${live ? 'bg-safe' : 'bg-ink-muted'}`}
                                                                        data-testid="connected-job-dot"
                                                                        aria-hidden
                                                                    />
                                                                    <span className="min-w-0 truncate">{job.name}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </TableCell>
                                        )}
                                        {/* Est. cost (admin only) — estimated total DeepContext
                                            scan cost for this blueprint (bd-deepcontext-jvqwp).
                                            Muted em dash when the blueprint has no job runs. */}
                                        {isAdmin && (
                                            <TableCell className="whitespace-nowrap align-middle text-sm tabular-nums" data-testid="est-scan-cost">
                                                {estScanCost === null ? (
                                                    <span className="text-ink-muted">—</span>
                                                ) : (
                                                    formatCurrency(estScanCost, 'USD')
                                                )}
                                            </TableCell>
                                        )}
                                        {/* Updated At */}
                                        <TableCell className="overflow-hidden truncate align-middle text-sm" data-testid="updated-at">
                                            {new Date(prompt.updatedAt).toLocaleDateString()}
                                        </TableCell>
                                        {/* Actions: ghost round icons */}
                                        <TableCell className="align-middle">
                                            <div className="flex items-center justify-end gap-1">
                                                {isAdmin && (
                                                    <ActionTooltip label="Delivery Funnel">
                                                        <ActionIconButton
                                                            kind="panel"
                                                            active={funnelOpen}
                                                            onClick={openFunnel}
                                                            aria-label="Delivery Funnel"
                                                        >
                                                            <FunnelIcon className="h-3.5 w-3.5" />
                                                        </ActionIconButton>
                                                    </ActionTooltip>
                                                )}
                                                {strategySimulatorEnabled && (
                                                    <ActionTooltip label="Strategy Simulator">
                                                        <ActionIconButton
                                                            kind="panel"
                                                            active={simulatorOpen}
                                                            onClick={openSimulator}
                                                            aria-label="Strategy Simulator"
                                                        >
                                                            <BeakerIcon className="h-3.5 w-3.5" />
                                                        </ActionIconButton>
                                                    </ActionTooltip>
                                                )}
                                                {canManagePrompt && (
                                                    <>
                                                        <ActionTooltip label="Edit">
                                                            <ActionIconButton
                                                                kind="edit"
                                                                onClick={() => onEdit(prompt)}
                                                                aria-label="Edit"
                                                            >
                                                                <PencilIcon className="h-3.5 w-3.5" />
                                                            </ActionIconButton>
                                                        </ActionTooltip>
                                                        <ActionTooltip align="end" label={isDeleteDisabled(prompt, user) ? 'Default blueprints cannot be deleted' : 'Delete'}>
                                                            <ActionIconButton
                                                                kind="delete"
                                                                onClick={() => onDelete(prompt.id)}
                                                                disabled={isDeleteDisabled(prompt, user)}
                                                                aria-label="Delete"
                                                                className="disabled:opacity-40 disabled:cursor-not-allowed"
                                                            >
                                                                <TrashIcon className="h-3.5 w-3.5" />
                                                            </ActionIconButton>
                                                        </ActionTooltip>
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                    {/* Expandable detail drawer — a canvas-inset band, visible
                                        only while a view panel is open; collapses when both
                                        close (page lock 7). */}
                                    {(simulatorOpen || (isAdmin && funnelOpen)) && (
                                        <tr>
                                            <td colSpan={colSpan} className="border-b border-line p-0">
                                                <div
                                                    data-testid="blueprint-drawer"
                                                    className="mx-2 mb-2 rounded-xl bg-canvas px-3.5 py-3"
                                                >
                                                    {/* View panels (mutually exclusive) */}
                                                    {simulatorOpen && (
                                                        <StrategySimulatorPanel
                                                            promptId={prompt.id}
                                                            initialCountryCodes={countryList}
                                                            simulatorEnabled={strategySimulatorEnabled}
                                                            embedded
                                                        />
                                                    )}
                                                    {isAdmin && funnelOpen && (
                                                        <DeliveryFunnelSection
                                                            blueprintId={prompt.id}
                                                            isExpanded
                                                            onOpenDomains={onOpenDomains}
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
        </TableFillBox>
    );
};

export default BlueprintsTable;
